import React, { useMemo, useState } from "react";
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions, Typography,
  TextField, MenuItem, FormControlLabel, Checkbox, Button,
} from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import RefreshIcon from "@mui/icons-material/Refresh";
import { NAVY, TEXT_SECONDARY } from "../../../theme/tokens";
import { PresetZone } from "./presets";
import MiniLayoutPreview from "./MiniLayoutPreview";
import {
  generateSmartLayout, smartLayoutName, SmartLayoutInput,
  PrimaryContent, LayoutPriority,
} from "./smartLayout";

/**
 * Rule-based Smart Layout generator UI (section 8). The user picks a few
 * high-level options; the local rules build a balanced, non-overlapping
 * layout that can be previewed, regenerated, then applied through the
 * normal preset flow (replace confirmation + undo). This is NOT AI — it is
 * deterministic; the AI entry point is separate and feature-flagged.
 */

interface SmartLayoutDialogProps {
  open: boolean;
  orientation: "landscape" | "portrait";
  onClose: () => void;
  onApply: (name: string, zones: PresetZone[]) => void;
}

const BUSINESS_TYPES = [
  "general", "restaurant", "cafe", "retail", "corporate", "healthcare",
];
const PRIMARY: Array<{ value: PrimaryContent; label: string }> = [
  { value: "video", label: "Video promotion" },
  { value: "image", label: "Image / poster" },
  { value: "menu", label: "Menu board" },
  { value: "website", label: "Website / news" },
];

const clampCount = (v: number) => Math.max(0, Math.min(4, Math.floor(v || 0)));

const SmartLayoutDialog: React.FC<SmartLayoutDialogProps> = ({ open, orientation, onClose, onApply }) => {
  const [businessType, setBusinessType] = useState("restaurant");
  const [primary, setPrimary] = useState<PrimaryContent>("video");
  const [images, setImages] = useState(2);
  const [videos, setVideos] = useState(1);
  const [includeText, setIncludeText] = useState(true);
  const [includeClock, setIncludeClock] = useState(false);
  const [includeWeather, setIncludeWeather] = useState(false);
  const [includeQr, setIncludeQr] = useState(true);
  const [priority, setPriority] = useState<LayoutPriority>("highlight");
  // A nonce lets "Regenerate" recompute even when inputs are unchanged
  // (the rules are deterministic, so it mainly re-confirms the same result).
  const [nonce, setNonce] = useState(0);

  const input: SmartLayoutInput = useMemo(() => ({
    orientation, businessType, primary,
    images, videos, includeText, includeClock, includeWeather, includeQr, priority,
  }), [orientation, businessType, primary, images, videos, includeText, includeClock, includeWeather, includeQr, priority]);

  // `nonce` is an intentional dependency so "Regenerate" recomputes on demand.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const zones = useMemo(() => generateSmartLayout(input), [input, nonce]);
  const aspect = orientation === "portrait" ? "9 / 16" : "16 / 9";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <AutoFixHighIcon /> Create Smart Layout
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
          {/* Options */}
          <Box>
            <TextField select size="small" fullWidth label="Business Type"
              value={businessType} onChange={(e) => setBusinessType(e.target.value)}
              sx={{ mb: 1.5 }}>
              {BUSINESS_TYPES.map((b) => (
                <MenuItem key={b} value={b}>{b.charAt(0).toUpperCase() + b.slice(1)}</MenuItem>
              ))}
            </TextField>

            <TextField select size="small" fullWidth label="Primary Content"
              value={primary} onChange={(e) => setPrimary(e.target.value as PrimaryContent)}
              sx={{ mb: 1.5 }}>
              {PRIMARY.map((p) => (
                <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
              ))}
            </TextField>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: 1.5 }}>
              <TextField size="small" label="Images" type="number"
                inputProps={{ min: 0, max: 4 }}
                value={images} onChange={(e) => setImages(clampCount(Number(e.target.value)))} />
              <TextField size="small" label="Videos" type="number"
                inputProps={{ min: 0, max: 4 }}
                value={videos} onChange={(e) => setVideos(clampCount(Number(e.target.value)))} />
            </Box>

            <Typography sx={{ fontSize: 12, fontWeight: 700, color: TEXT_SECONDARY, mb: 0.5 }}>
              Additional Widgets
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", mb: 1.5 }}>
              <FormControlLabel control={<Checkbox size="small" checked={includeQr}
                onChange={(e) => setIncludeQr(e.target.checked)} />} label="QR Code" />
              <FormControlLabel control={<Checkbox size="small" checked={includeClock}
                onChange={(e) => setIncludeClock(e.target.checked)} />} label="Clock" />
              <FormControlLabel control={<Checkbox size="small" checked={includeWeather}
                onChange={(e) => setIncludeWeather(e.target.checked)} />} label="Weather" />
              <FormControlLabel control={<Checkbox size="small" checked={includeText}
                onChange={(e) => setIncludeText(e.target.checked)} />} label="Text banner" />
            </Box>

            <TextField select size="small" fullWidth label="Priority"
              value={priority} onChange={(e) => setPriority(e.target.value as LayoutPriority)}>
              <MenuItem value="highlight">Highlight primary content</MenuItem>
              <MenuItem value="balanced">Balanced (equal areas)</MenuItem>
            </TextField>
          </Box>

          {/* Live preview */}
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: TEXT_SECONDARY, mb: 0.75 }}>
              Preview · {zones.length} zone{zones.length === 1 ? "" : "s"}
            </Typography>
            <MiniLayoutPreview zones={zones} aspect={aspect} showLabels />
            <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mt: 1 }}>
              Zones are non-overlapping percentages. You can fine-tune each
              zone after applying.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button startIcon={<RefreshIcon />} onClick={() => setNonce((n) => n + 1)}>
          Regenerate
        </Button>
        <Button variant="contained" color="secondary" sx={{ color: NAVY }}
          onClick={() => onApply(smartLayoutName(input), zones)}>
          Apply Layout
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SmartLayoutDialog;
